import { useState, useEffect, useMemo } from 'react';
import { api } from '../api';
import { CAP, useSession } from '../auth/session';
import {
  PageHeader, Card, Button, Pill, Field, Input,
  Table, THead, TH, TR, TD, Empty,
  fmtR, fmtDate, fmtDT, todayISO, thisMonthISO,
} from '../components/ui';

const ALL_TABS = [
  { id: 'rate-card', label: 'Rate card', caps: [CAP.VIEW_RATE_CARD] },
  // CTC tab is visible to anyone who can see *any* other Settings panel —
  // the values are masked server-side for callers without VIEW_CTC.
  { id: 'salaries',  label: 'CTC',       caps: [CAP.VIEW_RATE_CARD], anyOf: true,
    altCaps: [CAP.VIEW_CTC, CAP.VIEW_OVERHEAD] },
  { id: 'overhead',  label: 'Overhead',  caps: [CAP.VIEW_OVERHEAD] },
  { id: 'access',    label: 'Access',    caps: [CAP.EDIT_OVERHEAD], leadership: true },
];

export default function Settings() {
  const { user } = useSession();

  const tabs = useMemo(() => {
    if (!user) return [];
    return ALL_TABS.filter((t) => {
      if (t.leadership && user.role !== 'leadership') return false;
      const all = [...t.caps, ...(t.altCaps || [])];
      return all.some((c) => user.capabilities.has(c));
    });
  }, [user]);

  const [tab, setTab] = useState(tabs[0]?.id || null);
  useEffect(() => {
    if (tabs.length && !tabs.find((t) => t.id === tab)) {
      setTab(tabs[0].id);
    }
  }, [tabs, tab]);

  if (!tabs.length) {
    return (
      <div>
        <PageHeader eyebrow="Inputs" title="Settings" />
        <Card>
          <Empty>You don't have permission to view any Settings panels. Speak to an admin.</Empty>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="Inputs"
        title="Settings"
        subtitle="Capture sale rates, monthly CTC, and overhead pool. Every change is immutable — past versions stay queryable in the change log."
      />

      <nav className="flex gap-6 border-b border-slate-700 mb-8 -mt-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? 'border-brand-500 text-slate-100'
                : 'border-transparent text-slate-500 hover:text-slate-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === 'rate-card' && <RateCardTab />}
      {tab === 'salaries'  && <SalariesTab />}
      {tab === 'overhead'  && <OverheadTab />}
      {tab === 'access'    && <AccessTab />}
    </div>
  );
}

/* ─── Access ────────────────────────────────────────────────────────── */

const ROLE_OPTIONS = [
  { value: 'leadership', label: 'Leadership' },
  { value: 'manco',      label: 'Manco (PO / PM)' },
  { value: 'hr',         label: 'HR' },
  { value: 'dev-owner',  label: 'Dev Owner' },
  { value: 'dev-junior', label: 'Dev Junior' },
  { value: 'none',       label: '— No access —' },
];

function AccessTab() {
  const [rows, setRows] = useState([]);
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [filter, setFilter] = useState('');
  const [edits, setEdits] = useState({}); // email → { role, notes }
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = () => {
    api.admin.users().then((d) => setRows(d.rows || []));
    api.admin.audit().then((d) => setAudit(d.rows || []));
  };
  useEffect(load, []);

  const save = async () => {
    const work = Object.entries(edits);
    if (!work.length) return;
    setSaving(true);
    setMsg(null);
    try {
      let changed = 0;
      for (const [email, { role, notes }] of work) {
        const res = await api.admin.setRole(email, role, notes || null);
        if (res.status === 'ok') changed++;
      }
      setEdits({});
      load();
      setMsg({ kind: 'ok', text: `${changed} role change${changed === 1 ? '' : 's'} saved.` });
    } catch (e) {
      setMsg({ kind: 'err', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const filterFn = (row) =>
    !filter ||
    row.email.toLowerCase().includes(filter.toLowerCase()) ||
    (row.full_name || '').toLowerCase().includes(filter.toLowerCase()) ||
    (row.role || '').toLowerCase().includes(filter.toLowerCase());

  const dirty = Object.keys(edits).length > 0;

  return (
    <div className="space-y-6">
      <Card title="Manage access" subtitle="Grant or revoke roles per Platform45 user. Every change is captured in the audit log.">
        <div className="flex flex-wrap items-end gap-4">
          <Field label="Filter people" className="flex-1 min-w-48">
            <Input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Name, email or current role"
            />
          </Field>
          <Button disabled={!dirty || saving} onClick={save} loading={saving}>
            {dirty ? `Save ${Object.keys(edits).length} change${Object.keys(edits).length === 1 ? '' : 's'}` : 'No changes'}
          </Button>
        </div>
        {msg && (
          <p className={`text-xs mt-3 ${msg.kind === 'ok' ? 'text-emerald-600' : 'text-brand-500'}`}>
            {msg.text}
          </p>
        )}
      </Card>

      <Card title={`People · ${rows.length}`} padding="p-0">
        <Table>
          <THead>
            <TH>Name</TH>
            <TH>Email</TH>
            <TH>Department</TH>
            <TH>Current role</TH>
            <TH>New role</TH>
            <TH>Granted by</TH>
          </THead>
          <tbody>
            {rows.filter(filterFn).map((r) => {
              const newRole = edits[r.email]?.role ?? r.role ?? 'none';
              const original = r.role || 'none';
              const isDirty = newRole !== original;
              return (
                <TR key={r.email}>
                  <TD>{r.full_name || <span className="text-slate-500">—</span>}</TD>
                  <TD muted className="font-mono text-xs">{r.email}</TD>
                  <TD muted>{r.department || '—'}</TD>
                  <TD>
                    {r.role
                      ? <Pill tone="accent">{r.role}</Pill>
                      : <Pill tone="neutral">no access</Pill>}
                  </TD>
                  <TD>
                    <select
                      value={newRole}
                      onChange={(e) => setEdits((prev) => ({
                        ...prev,
                        [r.email]: { ...(prev[r.email] || {}), role: e.target.value },
                      }))}
                      className={`bg-slate-900 border rounded-md px-2 py-1.5 text-sm text-slate-100 ${
                        isDirty ? 'border-brand-500' : 'border-slate-700'
                      }`}
                    >
                      {ROLE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </TD>
                  <TD muted>
                    {r.granted_by || '—'}
                    {r.granted_at && (
                      <span className="block text-[11px] text-slate-500 mt-0.5">{fmtDate(r.granted_at)}</span>
                    )}
                  </TD>
                </TR>
              );
            })}
          </tbody>
        </Table>
      </Card>

      <Card
        title="Change log"
        subtitle={`${audit.length} entries · every role change ever made`}
        action={
          <Button variant="ghost" size="sm" onClick={() => setShowAudit((v) => !v)}>
            {showAudit ? 'Hide' : 'Show'}
          </Button>
        }
        padding={showAudit ? 'p-0' : 'p-6'}
      >
        {showAudit ? (
          <Table>
            <THead>
              <TH>Email</TH>
              <TH>From</TH>
              <TH>To</TH>
              <TH>Reason</TH>
              <TH>By</TH>
              <TH>At</TH>
            </THead>
            <tbody>
              {audit.length === 0 ? (
                <tr><td colSpan={6}><Empty>No history yet.</Empty></td></tr>
              ) : audit.map((r) => (
                <TR key={r.audit_id}>
                  <TD className="font-mono text-xs">{r.email}</TD>
                  <TD muted>{r.previous_role || <span className="text-slate-500">—</span>}</TD>
                  <TD><Pill tone={r.new_role === 'none' ? 'neutral' : 'accent'}>{r.new_role}</Pill></TD>
                  <TD muted className="max-w-xs truncate">{r.notes || ''}</TD>
                  <TD muted>{r.changed_by}</TD>
                  <TD muted mono>{fmtDT(r.changed_at)}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
        ) : (
          <p className="text-xs text-slate-500">Tap show to inspect every role change.</p>
        )}
      </Card>
    </div>
  );
}

/* ─── Rate card ──────────────────────────────────────────────────────── */

function RateCardTab() {
  const [active, setActive] = useState([]);
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [effectiveFrom, setEffectiveFrom] = useState(todayISO());
  const [notes, setNotes] = useState('');
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = () => {
    api.settings.getRateCard().then((d) => setActive(d.active || []));
    api.settings.auditRateCard().then((d) => setAudit(d.rows || []));
  };
  useEffect(load, []);

  const save = async () => {
    const rates = active.map((r) => ({
      role: r.role,
      sale_rate_per_hour: parseFloat(edits[r.role] ?? r.sale_rate_per_hour),
    }));
    if (rates.some((r) => Number.isNaN(r.sale_rate_per_hour))) {
      setMsg({ kind: 'err', text: 'All rates must be numeric.' });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      await api.settings.saveRateCard({
        effective_from: effectiveFrom,
        notes: notes || null,
        rates,
      });
      setEdits({});
      setNotes('');
      load();
      setMsg({ kind: 'ok', text: 'New rate card version published.' });
    } catch (e) {
      setMsg({ kind: 'err', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const dirty = Object.keys(edits).length > 0;

  return (
    <div className="space-y-6">
      <Card
        title="Active rate card"
        subtitle={`${active.length} roles · last updated ${
          active[0] ? fmtDate(active[0].effective_from) : '—'
        }`}
        action={<Pill tone={dirty ? 'warn' : 'neutral'}>{dirty ? `${Object.keys(edits).length} unsaved` : 'Saved'}</Pill>}
        padding="p-0"
      >
        <Table>
          <THead>
            <TH>Role</TH>
            <TH align="right">Sale rate · R / hour</TH>
            <TH>Effective from</TH>
            <TH>Captured by</TH>
          </THead>
          <tbody>
            {active.map((r) => (
              <TR key={r.rate_id}>
                <TD>{r.role}</TD>
                <TD align="right">
                  <Input
                    type="number"
                    step="any"
                    defaultValue={r.sale_rate_per_hour}
                    onChange={(e) =>
                      setEdits((prev) => ({ ...prev, [r.role]: e.target.value }))
                    }
                    className="w-32 text-right nums"
                  />
                </TD>
                <TD muted className="nums">{fmtDate(r.effective_from)}</TD>
                <TD muted>{r.created_by || '—'}</TD>
              </TR>
            ))}
          </tbody>
        </Table>
      </Card>

      <Card title="Publish new version">
        <div className="flex flex-wrap items-end gap-4">
          <Field label="Effective from">
            <Input
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
            />
          </Field>
          <Field label="Reason (optional)" className="flex-1 min-w-64">
            <Input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. FY26 adjustment after Q1 review"
            />
          </Field>
          <Button disabled={!dirty || saving} onClick={save} loading={saving}>
            {dirty ? `Publish ${Object.keys(edits).length} change${Object.keys(edits).length === 1 ? '' : 's'}` : 'No changes'}
          </Button>
        </div>
        {msg && (
          <p className={`text-xs mt-3 ${msg.kind === 'ok' ? 'text-emerald-600' : 'text-brand-500'}`}>
            {msg.text}
          </p>
        )}
      </Card>

      <Card
        title="Change log"
        subtitle={`${audit.length} entries · every rate ever published, newest first`}
        action={
          <Button variant="ghost" size="sm" onClick={() => setShowAudit((v) => !v)}>
            {showAudit ? 'Hide' : 'Show'}
          </Button>
        }
        padding={showAudit ? 'p-0' : 'p-6'}
      >
        {showAudit ? (
          <Table>
            <THead>
              <TH>Role</TH>
              <TH align="right">Rate</TH>
              <TH>From</TH>
              <TH>To</TH>
              <TH>By</TH>
              <TH>Reason</TH>
              <TH>Captured</TH>
            </THead>
            <tbody>
              {audit.length === 0 ? (
                <tr><td colSpan={7}><Empty>No history yet.</Empty></td></tr>
              ) : audit.map((r) => (
                <TR key={r.rate_id}>
                  <TD>{r.role}</TD>
                  <TD align="right" mono>{fmtR(r.sale_rate_per_hour)}</TD>
                  <TD muted mono>{fmtDate(r.effective_from)}</TD>
                  <TD muted mono>{fmtDate(r.effective_to)}</TD>
                  <TD muted>{r.created_by || '—'}</TD>
                  <TD muted className="max-w-xs truncate">{r.notes || ''}</TD>
                  <TD muted mono>{fmtDT(r.created_at)}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
        ) : (
          <p className="text-xs text-slate-500">Tap show to inspect all historical rate publications.</p>
        )}
      </Card>
    </div>
  );
}

/* ─── CTC / Salaries ────────────────────────────────────────────────── */

function SalariesTab() {
  const { user } = useSession();
  const canSeeCTC = user?.capabilities?.has(CAP.VIEW_CTC);

  const [active, setActive] = useState([]);
  const [unfilled, setUnfilled] = useState([]);
  const [audit, setAudit] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [effectiveFrom, setEffectiveFrom] = useState(todayISO());
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [filter, setFilter] = useState('');

  const load = () => {
    api.settings.getSalaries().then((d) => {
      setActive(d.active || []);
      setUnfilled(d.unfilled || []);
    });
    api.settings.auditSalaries().then((d) => setAudit(d.rows || []));
  };
  useEffect(load, []);

  const save = async () => {
    const entries = Object.entries(edits)
      .filter(([, v]) => v !== '' && !Number.isNaN(parseFloat(v)))
      .map(([employee_id, v]) => ({
        employee_id,
        monthly_salary: parseFloat(v),
      }));
    if (!entries.length) {
      setMsg({ kind: 'err', text: 'Nothing to save.' });
      return;
    }
    setSaving(true);
    setMsg(null);
    try {
      await api.settings.saveSalaries({
        effective_from: effectiveFrom,
        entries,
      });
      setEdits({});
      load();
      setMsg({ kind: 'ok', text: `${entries.length} CTC record${entries.length === 1 ? '' : 's'} saved.` });
    } catch (e) {
      setMsg({ kind: 'err', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const filterFn = (row) =>
    !filter ||
    row.full_name.toLowerCase().includes(filter.toLowerCase()) ||
    (row.role || '').toLowerCase().includes(filter.toLowerCase());

  const dirty = Object.keys(edits).length > 0;

  return (
    <div className="space-y-6">
      {canSeeCTC ? (
        <Card title="Publish CTC update">
          <div className="flex flex-wrap items-end gap-4">
            <Field label="Effective from">
              <Input
                type="date"
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
              />
            </Field>
            <Field label="Filter people" className="flex-1 min-w-48">
              <Input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Name or role"
              />
            </Field>
            <Button disabled={!dirty || saving} onClick={save} loading={saving}>
              {dirty ? `Save ${Object.keys(edits).length}` : 'No changes'}
            </Button>
          </div>
          {msg && (
            <p className={`text-xs mt-3 ${msg.kind === 'ok' ? 'text-emerald-600' : 'text-brand-500'}`}>
              {msg.text}
            </p>
          )}
        </Card>
      ) : (
        <Card>
          <p className="text-xs text-slate-400">CTC amounts are masked for your role. Names + roles are shown so you know who's on the books, but the rand figures are restricted to leadership and HR.</p>
        </Card>
      )}

      <Card
        title={`Active CTCs · ${active.length}`}
        subtitle="Current monthly CTC per active employee."
        padding="p-0"
      >
        <SalaryTable rows={active.filter(filterFn)} edits={edits} setEdits={setEdits} masked={!canSeeCTC} />
      </Card>

      {unfilled.length > 0 && (
        <Card
          title={`Unfilled · ${unfilled.length}`}
          subtitle="Active employees with no salary history yet. Cost-rate calculations skip these people."
          padding="p-0"
        >
          <SalaryTable
            rows={unfilled.filter(filterFn).map((r) => ({
              employee_id: r.employee_id,
              full_name: r.full_name,
              role: r.role,
              department: r.department,
              monthly_salary: r.reference_salary || 0,
              hourly_cost_rate: 0,
              effective_from: null,
              entered_by: null,
            }))}
            edits={edits}
            setEdits={setEdits}
          />
        </Card>
      )}

      <Card
        title="Change log"
        subtitle={canSeeCTC ? `${audit.length} entries · per-person CTC history` : 'Masked — leadership / HR only'}
        action={canSeeCTC ? (
          <Button variant="ghost" size="sm" onClick={() => setShowAudit((v) => !v)}>
            {showAudit ? 'Hide' : 'Show'}
          </Button>
        ) : null}
        padding={showAudit && canSeeCTC ? 'p-0' : 'p-6'}
      >
        {!canSeeCTC ? (
          <p className="text-xs text-slate-500">Change history is restricted to leadership and HR.</p>
        ) : showAudit ? (
          <Table>
            <THead>
              <TH>Name</TH>
              <TH>Role</TH>
              <TH align="right">Monthly</TH>
              <TH align="right">Hourly cost</TH>
              <TH>From</TH>
              <TH>To</TH>
              <TH>Captured by</TH>
              <TH>Captured at</TH>
            </THead>
            <tbody>
              {audit.length === 0 ? (
                <tr><td colSpan={8}><Empty>No history yet.</Empty></td></tr>
              ) : audit.map((r) => (
                <TR key={r.salary_id}>
                  <TD>{r.full_name}</TD>
                  <TD muted>{r.role}</TD>
                  <TD align="right" mono>{fmtR(r.monthly_salary)}</TD>
                  <TD align="right" muted mono>R {Number(r.hourly_cost_rate || 0).toFixed(2)}/h</TD>
                  <TD muted mono>{fmtDate(r.effective_from)}</TD>
                  <TD muted mono>{fmtDate(r.effective_to)}</TD>
                  <TD muted>{r.entered_by || '—'}</TD>
                  <TD muted mono>{fmtDT(r.created_at)}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
        ) : (
          <p className="text-xs text-slate-500">Tap show to see who changed what when.</p>
        )}
      </Card>
    </div>
  );
}

function SalaryTable({ rows, edits, setEdits, masked = false }) {
  if (!rows.length) return <Empty>No people match.</Empty>;
  if (masked) {
    return (
      <Table>
        <THead>
          <TH>Name</TH><TH>Role</TH><TH>Department</TH>
          <TH align="right">Current</TH><TH align="right">Hourly cost</TH>
        </THead>
        <tbody>
          {rows.map((r) => (
            <TR key={r.employee_id}>
              <TD>{r.full_name}</TD>
              <TD muted>{r.role}</TD>
              <TD muted>{r.department}</TD>
              <TD align="right" mono muted title="Restricted to leadership / HR">•••</TD>
              <TD align="right" mono muted>•••</TD>
            </TR>
          ))}
        </tbody>
      </Table>
    );
  }
  return (
    <Table>
      <THead>
        <TH>Name</TH>
        <TH>Role</TH>
        <TH>Department</TH>
        <TH align="right">Current</TH>
        <TH align="right">New CTC</TH>
        <TH align="right">Hourly cost</TH>
        <TH>From</TH>
        <TH>By</TH>
      </THead>
      <tbody>
        {rows.map((r) => {
          const v = edits[r.employee_id];
          const newSalary = v === undefined || v === '' ? null : parseFloat(v);
          const newHourly = newSalary === null ? null : (newSalary / 160).toFixed(2);
          return (
            <TR key={r.employee_id}>
              <TD>{r.full_name}</TD>
              <TD muted>{r.role}</TD>
              <TD muted>{r.department}</TD>
              <TD align="right" mono>{fmtR(r.monthly_salary)}</TD>
              <TD align="right">
                <Input
                  type="number"
                  step="any"
                  placeholder="—"
                  value={edits[r.employee_id] ?? ''}
                  onChange={(e) =>
                    setEdits((prev) => ({ ...prev, [r.employee_id]: e.target.value }))
                  }
                  className="w-28 text-right nums"
                />
              </TD>
              <TD align="right" muted mono>{newHourly ? `R ${newHourly}/h` : '—'}</TD>
              <TD muted mono>{fmtDate(r.effective_from)}</TD>
              <TD muted>{r.entered_by || '—'}</TD>
            </TR>
          );
        })}
      </tbody>
    </Table>
  );
}

/* ─── Overhead ───────────────────────────────────────────────────────── */

function OverheadTab() {
  const [rows, setRows] = useState([]);
  const [showAudit, setShowAudit] = useState(false);
  const [audit, setAudit] = useState([]);
  const [form, setForm] = useState({
    period_month: thisMonthISO(),
    non_billable_salaries: '',
    opex_admin: '',
    overhead_rate_per_dev: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = () => {
    api.settings.getOverhead().then((d) => setRows(d.rows || []));
    api.settings.auditOverhead().then((d) => setAudit(d.rows || []));
  };
  useEffect(load, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      await api.settings.saveOverhead({
        period_month: form.period_month,
        non_billable_salaries: form.non_billable_salaries ? parseFloat(form.non_billable_salaries) : null,
        opex_admin: form.opex_admin ? parseFloat(form.opex_admin) : null,
        overhead_rate_per_dev: parseFloat(form.overhead_rate_per_dev),
        notes: form.notes || null,
      });
      load();
      setMsg({ kind: 'ok', text: `Overhead saved for ${form.period_month}.` });
    } catch (e) {
      setMsg({ kind: 'err', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card title="Record monthly overhead">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <Field label="Month">
            <Input type="month" value={form.period_month} onChange={set('period_month')} />
          </Field>
          <Field label="Non-billable salaries">
            <Input type="number" value={form.non_billable_salaries} onChange={set('non_billable_salaries')} placeholder="R" />
          </Field>
          <Field label="Opex / admin">
            <Input type="number" value={form.opex_admin} onChange={set('opex_admin')} placeholder="R" />
          </Field>
          <Field label="Rate per dev">
            <Input type="number" value={form.overhead_rate_per_dev} onChange={set('overhead_rate_per_dev')} placeholder="R" />
          </Field>
          <Button onClick={save} loading={saving} disabled={!form.overhead_rate_per_dev}>Save month</Button>
        </div>
        <Field label="Reason (optional)" className="mt-4">
          <Input type="text" value={form.notes} onChange={set('notes')} placeholder="e.g. Office expansion adjustment" />
        </Field>
        {msg && (
          <p className={`text-xs mt-3 ${msg.kind === 'ok' ? 'text-emerald-600' : 'text-brand-500'}`}>
            {msg.text}
          </p>
        )}
      </Card>

      <Card title={`History · ${rows.length} months`} padding="p-0">
        {rows.length === 0 ? <Empty>No overhead recorded yet.</Empty> : (
          <Table>
            <THead>
              <TH>Month</TH>
              <TH align="right">Non-bill salaries</TH>
              <TH align="right">Opex / admin</TH>
              <TH align="right">Pool</TH>
              <TH align="right"># devs</TH>
              <TH align="right">Rate / dev</TH>
              <TH align="right">12-mo rolling</TH>
              <TH>By</TH>
            </THead>
            <tbody>
              {rows.map((r) => (
                <TR key={r.overhead_id}>
                  <TD mono>{r.period_month}</TD>
                  <TD align="right" muted mono>{fmtR(r.non_billable_salaries)}</TD>
                  <TD align="right" muted mono>{fmtR(r.opex_admin)}</TD>
                  <TD align="right" mono>{fmtR(r.total_pool)}</TD>
                  <TD align="right" muted mono>{r.number_of_devs}</TD>
                  <TD align="right" mono>{fmtR(r.overhead_rate_per_dev)}</TD>
                  <TD align="right" muted mono>{fmtR(r.rolling_12m_rate)}</TD>
                  <TD muted>{r.created_by || '—'}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card
        title="Change log"
        action={
          <Button variant="ghost" size="sm" onClick={() => setShowAudit((v) => !v)}>
            {showAudit ? 'Hide' : 'Show'}
          </Button>
        }
        padding={showAudit ? 'p-0' : 'p-6'}
      >
        {showAudit ? (
          <Table>
            <THead>
              <TH>Month</TH>
              <TH align="right">Rate / dev</TH>
              <TH>Reason</TH>
              <TH>By</TH>
              <TH>Captured at</TH>
            </THead>
            <tbody>
              {audit.length === 0 ? (
                <tr><td colSpan={5}><Empty>No history yet.</Empty></td></tr>
              ) : audit.map((r) => (
                <TR key={r.overhead_id}>
                  <TD mono>{r.period_month}</TD>
                  <TD align="right" mono>{fmtR(r.overhead_rate_per_dev)}</TD>
                  <TD muted className="max-w-xs truncate">{r.notes || ''}</TD>
                  <TD muted>{r.created_by || '—'}</TD>
                  <TD muted mono>{fmtDT(r.created_at)}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
        ) : (
          <p className="text-xs text-slate-500">Tap show to inspect every overhead capture.</p>
        )}
      </Card>
    </div>
  );
}
